import { FC, KeyboardEvent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AutoAwesome, CheckCircleOutline } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { unwrapResult } from '@reduxjs/toolkit';

import { Text } from '../../../shared/uiComponents';
import { CustomizedInput } from '../../../shared/uiComponents/Input';
import { GREEN, PRIMARY, RED, TEXT } from '../../../shared/Helpers/colors';
import { AiParsedForm } from './types';
import { DispatchProperties } from '../../../redux/store';
import { parseScheduleWithAi } from '../../../redux/State/clientSlice/recurringScheduleSlice';

const MIN_PROMPT_LENGTH = 15;
const MAX_PROMPT_LENGTH = 2000;

interface AiContext {
	clientId?: string;
	providers?: string[];
	sessionTypes?: string[];
}

interface AiPromptBoxProperties {
	context: AiContext;
	disabled?: boolean;
	onParsed: (parsed: AiParsedForm) => void;
}

const AiPromptBox: FC<AiPromptBoxProperties> = ({ context, onParsed, disabled = false }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const [prompt, setPrompt] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>('');
	const [success, setSuccess] = useState<boolean>(false);

	const submit = async () => {
		if (loading) return;

		const trimmed = prompt.trim();

		if (trimmed.length < MIN_PROMPT_LENGTH) {
			setSuccess(false);
			setError(`Please enter at least ${MIN_PROMPT_LENGTH} characters describing the schedule.`);

			return;
		}

		setLoading(true);
		setError('');
		setSuccess(false);

		try {
			const parsed = await dispatch(parseScheduleWithAi({
				prompt: trimmed,
				clientId: context.clientId,
				providers: context.providers || [],
				sessionTypes: context.sessionTypes || [],
			})).then(unwrapResult);
			onParsed(parsed);
			setSuccess(true);
		} catch (e) {
			setError(e instanceof Error ? e.message : (e as { title?: string })?.title || 'Failed to parse the request');
		} finally {
			setLoading(false);
		}
	};

	const handleClear = () => {
		setPrompt('');
		setError('');
		setSuccess(false);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			submit();
		}
	};

	return (
		<div className="aiPromptBox">
			<div className="aiPromptHeader">
				<div className="aiPromptIcon">
					<AutoAwesome fontSize="small" style={{ color: PRIMARY.BASE }} />
				</div>
				<div>
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<Text title="Fill with AI" type="psm" fontWeight="semiBold" />
						<span className="betaChip">Beta</span>
					</div>
					<Text
						title="Describe the schedule in plain English and press Enter, or just enter the schedule info in the fields below."
						type="pxs"
						color={TEXT.TERTIARY}
					/>
				</div>
			</div>

			<CustomizedInput
				multiline
				minRows={2}
				value={prompt}
				disabled={disabled || loading}
				onChange={(e) => {
					setPrompt(e.target.value.slice(0, MAX_PROMPT_LENGTH));
					if (success) setSuccess(false);
					if (error) setError('');
				}}
				onKeyDown={handleKeyDown}
				placeholder="e.g. BT Sarah for Direct Therapy on weekdays 9-11am starting next Monday until end of authorization."
				endAdornment={loading ? <CircularProgress size={18} style={{ color: PRIMARY.BASE }} /> : null}
				inputProps={{ maxLength: MAX_PROMPT_LENGTH }}
			/>

			{Boolean(prompt) && (
				<button type="button" className="aiPromptClearButton" onClick={handleClear} disabled={disabled || loading}>
					Clear prompt
				</button>
			)}

			<div aria-live="assertive">
				{Boolean(error) && <Text title={error} type="pxs" color={RED.BASE} className="aiPromptError" />}
			</div>

			<div aria-live="polite">
				{success && (
					<div className="aiPromptSuccess">
						<CheckCircleOutline fontSize="small" style={{ color: GREEN.BASE }} />
						<Text title="Schedule fields filled from AI. Review and adjust below." type="pxs" color={GREEN.BASE} />
					</div>
				)}
			</div>
		</div>
	);
};

export default AiPromptBox;
